/**
 * Battleship AI Engine
 * Implements Parity Hunt & Target Strategy
 */
import { GRID_SIZE, BattleshipEngine } from './engine.js';

export class BattleshipAI {
  constructor() {
    this.engine = new BattleshipEngine();
    this.targetQueue = [];
    this.hitStack = [];
    this.reset();
  }

  reset() {
    this.engine.reset();
    this.engine.generateRandomFleet();
    this.targetQueue = [];
    this.hitStack = [];
    this.firedCoords = new Set();
  }

  getNextShot() {
    // 1. Target Mode: if we have pending targets around a known unsunk hit
    while (this.targetQueue.length > 0) {
      const target = this.targetQueue.shift();
      const key = `${target.x},${target.y}`;
      if (!this.firedCoords.has(key)) {
        this.firedCoords.add(key);
        return target;
      }
    }

    // 2. Hunt Mode: parity search (checkerboard cells)
    const huntCandidates = [];
    for (let y = 0; y < GRID_SIZE; y++) {
      for (let x = 0; x < GRID_SIZE; x++) {
        const key = `${x},${y}`;
        if (!this.firedCoords.has(key) && (x + y) % 2 === 0) {
          huntCandidates.push({ x, y });
        }
      }
    }

    let chosen = null;
    if (huntCandidates.length > 0) {
      chosen = huntCandidates[Math.floor(Math.random() * huntCandidates.length)];
    } else {
      // Remaining odd cells
      const remaining = [];
      for (let y = 0; y < GRID_SIZE; y++) {
        for (let x = 0; x < GRID_SIZE; x++) {
          const key = `${x},${y}`;
          if (!this.firedCoords.has(key)) {
            remaining.push({ x, y });
          }
        }
      }
      chosen = remaining[Math.floor(Math.random() * remaining.length)] || { x: 0, y: 0 };
    }

    this.firedCoords.add(`${chosen.x},${chosen.y}`);
    return chosen;
  }

  recordShotResult(x, y, hit, sunkShip) {
    if (hit) {
      this.hitStack.push({ x, y });

      if (sunkShip) {
        // Ship is sunk, clear targets related to this ship
        this.hitStack = [];
        this.targetQueue = [];
      } else {
        // Add orthogonal neighbors to target queue
        const neighbors = [
          { x: x + 1, y },
          { x: x - 1, y },
          { x, y: y + 1 },
          { x, y: y - 1 }
        ];

        for (const n of neighbors) {
          if (n.x >= 0 && n.x < GRID_SIZE && n.y >= 0 && n.y < GRID_SIZE) {
            const key = `${n.x},${n.y}`;
            if (!this.firedCoords.has(key)) {
              this.targetQueue.push(n);
            }
          }
        }
      }
    }
  }
}
